import React, { useState } from "react";
import cs from "classnames";
import { Form } from "reactstrap";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";

import { useWorkplacesClient } from "../../../../api/grpc/workplaces/useWorkplacesClient";
import { useTimeZoneContext } from "../../../../lib/context/TimeZone/TimeZoneContext";

import { EditDeviceFormik } from "./lib/useEditDeviceFormik";
import { DeviceInformation } from "../../../Workplaces/EditWorkplace/EditDevice/types";
import {
  DeviceModalTab,
  getDeviceModalPanelId,
  getDeviceModalTabId,
} from "../../../Workplaces/EditWorkplace/EditDevice/components/DeviceModalTabs";
import { GeneralSection } from "./sections/GeneralSection";
import { DateTimeSection } from "./sections/DateTimeSection";
import { HardwareSection } from "./sections/HardwareSection";
import { DeviceActions } from "./sections/DeviceActions";
import { SoftwareSection } from "./sections/SoftwareSection";
import { PairedSensors, getSensorsState } from "./sections/PairedSensors";
import { SetDeviceTimeButton } from "../../../Devices/DeviceTimeWizard/SetDeviceTimeButton";

import "./DeviceForm.scss";

export type DeviceWorkspaceType = "roomId" | "wayFinderId";

interface Props {
  id: string;
  formik: EditDeviceFormik;
  isWayfinderDevice: boolean;
  deviceInfo: DeviceInformation | null;
  deviceId: string;
  workspaceId?: string;
  shouldUpdateDevice: boolean;
  appVersion?: string;
  lastDevicePing?: number;
  isOnline?: boolean;
  activeTab: DeviceModalTab;
  // Only devices that support date & time settings show the section
  showDateTime: boolean;
  // Only devices that support remote restart show the button
  showRestart: boolean;
  onRestart: () => void;
  refetch?: () => void;
}

export const EditDeviceForm = (props: Props) => {
  const {
    id,
    formik,
    isWayfinderDevice,
    deviceInfo,
    deviceId,
    workspaceId,
    shouldUpdateDevice,
    appVersion,
    isOnline,
    lastDevicePing,
    activeTab,
    showDateTime,
    showRestart,
    onRestart,
    refetch,
  } = props;
  const { timeZoneFormatted, timeZone } = useTimeZoneContext();
  const utc = timeZoneFormatted.split(" ")[0];
  const definedUtc = utc.replace(":00", "");

  const { updateDeviceVersion } = useWorkplacesClient();

  const [isUpdating, setIsUpdating] = useState(false);

  const pushAvailable = isOnline || isOnline === null;
  const sensorLastSync = deviceInfo?.sensorLastSync
    ? format(
        toZonedTime(
          new Date(
            typeof deviceInfo.sensorLastSync === "number"
              ? deviceInfo.sensorLastSync * 1000
              : deviceInfo.sensorLastSync
          ),
          timeZone
        ),
        "d MMM yyyy, HH:mm"
      ) +
      " " +
      definedUtc
    : "Unknown";

  const handleUpdateDevice = async () => {
    if (!shouldUpdateDevice) {
      toast.warn("Device already running the latest version.");
      return;
    }

    try {
      setIsUpdating(true);

      await updateDeviceVersion({
        deviceId: deviceId,
      });

      toast.success(
        "Updating version of device. Note that this may take several hours to confirm."
      );
    } catch (error: any) {
      toast.error(error?.message ?? "Coulnd't update version of device!");
    } finally {
      setTimeout(() => {
        setIsUpdating(false);
      }, 2000);
    }
  };

  return (
    <Form className="DeviceForm">
      <div
        role="tabpanel"
        id={getDeviceModalPanelId(activeTab)}
        aria-labelledby={getDeviceModalTabId(activeTab)}
        className={cs("DeviceModal__panel", {
          "DeviceModal__panel--single-column":
            activeTab === "settings" && !showDateTime,
        })}
      >
        {activeTab === "settings" ? (
          <>
            <div className="DeviceModal__column">
              <GeneralSection
                formik={formik}
                isWayfinderDevice={isWayfinderDevice}
                status={
                  deviceInfo
                    ? {
                        pushAvailable,
                        lastSync: lastDevicePing
                          ? format(
                              toZonedTime(
                                new Date(lastDevicePing * 1000),
                                timeZone
                              ),
                              "d MMM yyyy, HH:mm"
                            ) +
                            " " +
                            definedUtc
                          : "Unknown",
                      }
                    : null
                }
              />
            </div>
            {showDateTime && (
              <div className="DeviceModal__column DeviceModal__column--date-time">
                <DateTimeSection formik={formik} />
                <SetDeviceTimeButton variant="text" refetch={refetch} />
              </div>
            )}
          </>
        ) : (
          <>
            <div className="DeviceModal__column">
              <HardwareSection hardware={deviceInfo?.hardwareInfo ?? null} />

              <DeviceActions
                id={id}
                deviceId={deviceId}
                deviceName={formik.initialValues.name}
                workspaceId={workspaceId}
                showRestart={showRestart}
                pushAvailable={pushAvailable}
                onRestart={onRestart}
              />
            </div>
            <div className="DeviceModal__column">
              <SoftwareSection
                formik={formik}
                appName={deviceInfo ? deviceInfo.softwareInfo.App : null}
                appVersion={appVersion ?? ""}
                shouldUpdate={shouldUpdateDevice}
                isUpdating={isUpdating}
                pushAvailable={pushAvailable}
                onUpdate={handleUpdateDevice}
              />

              {!isWayfinderDevice && (
                <PairedSensors
                  sensorsState={getSensorsState(
                    deviceInfo?.sensorInfo ?? null,
                    pushAvailable
                  )}
                  sensors={deviceInfo?.sensorInfo ?? []}
                  lastSync={sensorLastSync}
                />
              )}
            </div>
          </>
        )}
      </div>
      {/* <FormSection title="Hardwarestatus">
        <FormGroup>
          <Label for="brand">Brand</Label>
          <Input readOnly type="text" id="brand" name="brand" value="Android" />
        </FormGroup>
        <FormGroup>
          <Label for="description">OS</Label>
          <Input
            readOnly
            type="text"
            id="description"
            name="description"
            value="Android 10"
          />
        </FormGroup>
      </FormSection> */}
    </Form>
  );
};
